import hyperid from 'hyperid';
import { flatMap, fromPairs, zip, map } from 'lodash';
import { AnnotationType } from './decode';
import { NodeType, TriggerType } from './types/common';
const DEFAULT_ANNOTATIONS_MAP = {
    [AnnotationType.dataTrigger]: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@v12.1.4/assets/72x72/26a1.png',
    [AnnotationType.tickTrigger]: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@v12.1.4/assets/72x72/23f1.png',
    [AnnotationType.loopDef]: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@12.1.4/assets/72x72/27b0.png'
};
const generateId = hyperid();
function decode(network, options = {}) {
    const { plugCache = [], annotationsMap } = options;
    const sensors = map(network.sensors, createSensorNode);
    const actuators = map(network.actuators, createActuatorNode);
    const gates = map(network.relations, createGateNode);
    const nodes = [...sensors, ...actuators, ...gates];
    const triggers = flatMap(network.triggers, createTriggerEdges);
    const relations = flatMap(network.relations, createRelationEdges);
    const edges = [...triggers, ...relations];
    const notes = map(network.notes, createNoteNode);
    return {
        name: network.name,
        elements: [...nodes, ...edges, ...notes]
    };
    function createSensorNode(sensor) {
        const id = generateId();
        const { label, position, dataTrigger, tickTrigger, loopDef, resource } = sensor;
        const description = `${sensor.name} v${sensor.version}`;
        const [x, y] = position || [0, 0];
        const annotations = [];
        const labels = [];
        const getAnnotation = (type) => (annotationsMap || DEFAULT_ANNOTATIONS_MAP)[type];
        if (dataTrigger) {
            annotations.push(getAnnotation(AnnotationType.dataTrigger));
        }
        if (tickTrigger) {
            annotations.push(getAnnotation(AnnotationType.tickTrigger));
        }
        if (loopDef) {
            annotations.push(getAnnotation(AnnotationType.loopDef));
        }
        if (resource) {
            labels.push(resource);
        }
        const plug = findPlug('sensor', sensor.name);
        const configurationProperties = plugConfigurationToObject(plug.configuration);
        const properties = Object.assign(Object.assign({}, configurationProperties), sensor.properties);
        return {
            group: 'nodes',
            data: {
                id,
                icon: plug === null || plug === void 0 ? void 0 : plug.iconURL,
                type: NodeType.SENSOR,
                label,
                description,
                plug: {
                    name: sensor.name,
                    version: sensor.version
                },
                configuration: {
                    properties,
                    resource: sensor.resource,
                    sequence: sensor.sequence,
                    dataTrigger: sensor.dataTrigger,
                    tickTrigger: sensor.tickTrigger,
                    loopDef: sensor.loopDef,
                    evictionTime: sensor.evictionTime,
                    pollingPeriod: sensor.pollingPeriod,
                    timeout: sensor.timeout,
                    timeoutState: sensor.timeoutState
                },
                states: plug.states,
                annotations,
                labels
            },
            position: { x, y }
        };
    }
    function createActuatorNode(actuator) {
        const id = generateId();
        const { name, version, label, position } = actuator;
        const [x, y] = position || [0, 0];
        const description = `${name} v${version}`;
        const annotations = [];
        const plug = findPlug('actuator', actuator.name);
        const configurationProperties = plugConfigurationToObject(plug.configuration);
        const properties = Object.assign(Object.assign({}, configurationProperties), actuator.properties);
        return {
            group: 'nodes',
            data: {
                id,
                icon: plug === null || plug === void 0 ? void 0 : plug.iconURL,
                type: NodeType.ACTUATOR,
                label,
                description,
                configuration: {
                    properties,
                    timeout: actuator.timeout,
                    timeoutState: actuator.timeoutState
                },
                plug: {
                    name,
                    version
                },
                annotations
            },
            position: { x, y }
        };
    }
    function createNoteNode(note) {
        const id = generateId();
        const { position, text } = note;
        const [x, y] = position || [0, 0];
        return {
            group: 'nodes',
            data: {
                id,
                type: NodeType.NOTE,
                label: text
            },
            position: { x, y }
        };
    }
    function createGateNode(relation) {
        const id = generateId();
        const gateType = relation.type;
        const { label, position } = relation;
        const [x, y] = position || [0, 0];
        return {
            group: 'nodes',
            data: {
                id,
                label,
                type: NodeType.GATE,
                gateType
            },
            position: { x, y }
        };
    }
    function createTriggerEdges(trigger) {
        const { sourceLabel, destinationLabel } = trigger;
        const sourceNode = findNodeByLabel(sourceLabel);
        const sourceId = sourceNode.data.id;
        const targetNode = findNodeByLabel(destinationLabel);
        const targetId = targetNode.data.id;
        const elements = [];
        if (trigger.statesTrigger) {
            const edge = createEdge(sourceId, targetId, {
                triggerType: TriggerType.STATES,
                states: trigger.statesTrigger
            });
            elements.push(edge);
        }
        if (trigger.stateChangeTrigger) {
            const memoryBlockNode = createMemoryBlock(trigger);
            const memoryBlockNodeId = memoryBlockNode.data.id;
            const { stateFrom, stateTo } = trigger.stateChangeTrigger;
            const starToStar = stateFrom === '*' && stateTo === '*';
            const sourceEdge = createEdge(sourceId, memoryBlockNodeId, {
                triggerType: TriggerType.STATE_CHANGE_DECISION,
                // not entirely sure why but for * -> * we have to return
                // undefined for the graph editor to draw it properly
                states: starToStar ? undefined : [stateFrom, stateTo]
            });
            const targetEdge = createEdge(memoryBlockNodeId, targetId);
            elements.push(memoryBlockNode, sourceEdge, targetEdge);
        }
        return elements;
    }
    function createEdge(source, target, edgeProps) {
        const id = generateId();
        return {
            group: 'edges',
            data: Object.assign({ id,
                source,
                target }, edgeProps)
        };
    }
    function createMemoryBlock(trigger) {
        if (!trigger.stateChangeTrigger) {
            throw new Error('Trigger does not have a state change');
        }
        const id = generateId();
        const { sourceLabel, destinationLabel, stateChangeTrigger } = trigger;
        const { stateFrom, stateTo } = stateChangeTrigger;
        const sourceNode = findNodeByLabel(sourceLabel);
        const targetNode = findNodeByLabel(destinationLabel);
        const position = {
            x: targetNode.position.x - 150,
            y: targetNode.position.y
        };
        return {
            group: 'nodes',
            data: {
                id,
                label: `${stateFrom} → ${stateTo}`,
                type: NodeType.MEMORY_BLOCK,
                stateFrom,
                stateTo,
                sourceId: sourceNode.data.id,
                targetId: targetNode.data.id
            },
            position
        };
    }
    function createRelationEdges(relation) {
        const sourceIds = relation.parentLabels.map((label) => findNodeByLabel(label).data.id);
        const targetId = findNodeByLabel(relation.label).data.id;
        const activeStatesPerSource = zip(...relation.combinations);
        const edges = sourceIds.map((source, index) => {
            const states = activeStatesPerSource[index];
            return createEdge(source, targetId, {
                triggerType: TriggerType.STATES,
                states
            });
        });
        return edges;
    }
    function findPlug(type, name) {
        const plug = plugCache.find((plug) => plug.type === type && plug.name === name);
        if (!plug)
            throw new Error(`Missing plug ${name}`);
        return plug;
    }
    function findNodeByLabel(label) {
        return nodes.find((n) => n.data.label === label);
    }
    function plugConfigurationToObject(configuration) {
        return fromPairs(configuration.map((item) => [item.name, '']));
    }
}
export default decode;
//# sourceMappingURL=decode-simplified.js.map